import { useRef, useState, type MouseEvent, type SyntheticEvent } from "react";

import { CroppedImage } from "@/components/waste/cropped-image";
import { resolveCrop, zoomOrigin, type BoundingArea, type Natural } from "@/lib/image-crop";
import { cn } from "@/lib/utils";

/** How far a hovered capture magnifies. Shared so the lightbox and the labelling
 * workstation can never drift apart on it. */
const ZOOM = 2.5;

type Props = {
  src: string;
  alt: string;
  /** The capturing device's crop box (POS2-1105) — the zoom stays inside it. */
  box: BoundingArea | null;
  /** Chrome for the frame the zoom happens inside: border, radius, background. */
  frameClassName?: string;
  /** Classes for the image itself. */
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
};

/**
 * A capture that magnifies at the cursor while the pointer is over it.
 *
 * Pointer-driven and mouse-only, exactly as the lightbox has always been — touch
 * and keyboard zoom are deliberately not built here (POS2-1106). It takes no focus
 * and binds no keys, so the workstation's C/R/D/U hotkeys are untouched.
 *
 * The zoom composes with the crop rather than replacing it: POS2-1105 places the
 * image with a `translate` and leaves `transform` open on the end, and the origin
 * is mapped through the box so the point under the cursor is the point that stays
 * put. Without that mapping a cropped capture would zoom to somewhere else in the
 * frame entirely.
 */
export function MagnifiableImage({
  src,
  alt,
  box,
  frameClassName,
  className,
  onLoad,
  onError,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [origin, setOrigin] = useState<string | null>(null);
  const [natural, setNatural] = useState<Natural | null>(null);

  function handleMove(e: MouseEvent<HTMLDivElement>) {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const fx = (e.clientX - rect.left) / rect.width;
    const fy = (e.clientY - rect.top) / rect.height;
    setOrigin(zoomOrigin(fx, fy, resolveCrop(box, natural), natural));
  }

  function handleLoad(e: SyntheticEvent<HTMLImageElement>) {
    setNatural({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight });
    onLoad?.();
  }

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={() => setOrigin(null)}
      // Clips the magnified image to the frame — without it a scaled capture
      // spills over whatever sits beside it.
      className={cn("cursor-zoom-in overflow-hidden", frameClassName)}
      data-testid="magnifiable-image"
    >
      <CroppedImage
        src={src}
        alt={alt}
        box={box}
        // Dragging an image out of the page while magnifying it is never the
        // intent, so this is not a caller's choice.
        draggable={false}
        onLoad={handleLoad}
        onError={onError}
        transform={origin ? `scale(${ZOOM})` : "scale(1)"}
        transformOrigin={origin ?? "center"}
        className={className}
      />
    </div>
  );
}
